import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Alert from '../ui/Alert';
import './RecipientManager.css';

const RecipientManager = ({ 
  recipients = [], 
  onAddRecipient, 
  onRemoveRecipient, 
  onContinue,
  onUpdateRecipient,
  currentUser,
  loading = false
}) => {
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '', message: '' });
  const [error, setError] = useState(null);
  const [includeSelf, setIncludeSelf] = useState(false);
  
  // Check if current user is already in recipients
  useEffect(() => {
    if (currentUser && recipients.some(r => r.email === currentUser.email)) {
      setIncludeSelf(true);
    }
  }, [currentUser, recipients]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecipient(prev => ({ ...prev, [name]: value }));
  };
  
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  const handleAddRecipient = () => {
    // Validate inputs
    if (!newRecipient.name.trim()) {
      setError('Please enter recipient name');
      return;
    }
    
    if (!newRecipient.email.trim()) {
      setError('Please enter recipient email');
      return;
    }
    
    if (!validateEmail(newRecipient.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Check for duplicate email
    if (recipients.some(r => r.email.toLowerCase() === newRecipient.email.toLowerCase())) {
      setError('This email is already added as a recipient');
      return;
    }
    
    // Add recipient
    onAddRecipient(newRecipient);
    
    // Reset form
    setNewRecipient({ name: '', email: '', message: '' });
    setError(null);
  };
  
  const handleRemoveRecipient = (index) => {
    // If removing the current user, update includeSelf state
    if (currentUser && recipients[index].email === currentUser.email) {
      setIncludeSelf(false);
    }
    onRemoveRecipient(index);
  };
  
  const handleIncludeSelfChange = (e) => {
    const checked = e.target.checked;
    setIncludeSelf(checked);
    
    if (checked && currentUser) {
      // Add current user as a recipient if not already present
      if (!recipients.some(r => r.email === currentUser.email)) {
        const userFullName = currentUser.user_metadata?.full_name || 'You';
        onAddRecipient({
          name: userFullName,
          email: currentUser.email,
          message: '',
          isSelf: true
        });
      }
    } else {
      // Remove current user from recipients
      const currentUserIndex = recipients.findIndex(r => r.email === currentUser?.email);
      if (currentUserIndex !== -1) {
        onRemoveRecipient(currentUserIndex);
      }
    }
  };
  
  const handleContinue = () => {
    // Filter out any empty recipients before continuing
    const validRecipients = recipients.filter(r => r.email && r.email.trim() !== '');
    
    if (validRecipients.length === 0) {
      setError('Please add at least one recipient');
      return;
    }
    
    onContinue(validRecipients);
  };
  
  return (
    <div className="recipient-manager">
      <h2 className="recipient-manager-title">
        Who needs to sign this document?
      </h2>
      
      {error && (
        <Alert 
          type="error"
          message={error}
          dismissible={true}
          onDismiss={() => setError(null)}
        />
      )}
      
      {currentUser && (
        <Card className="self-sign-card">
          <div className="self-sign-option">
            <input
              type="checkbox"
              id="includeSelf"
              checked={includeSelf}
              onChange={handleIncludeSelfChange}
              className="self-sign-checkbox"
            />
            <label htmlFor="includeSelf" className="self-sign-label">
              I need to sign this document myself
            </label>
          </div>
          {includeSelf && (
            <div className="self-sign-info">
              You'll be able to sign the document after adding signature fields.
              {recipients.length > 1 && " Other signatories will receive the document after you've signed it."}
            </div>
          )}
        </Card>
      )}
      
      <Card className="add-recipient-card">
        <h3 className="add-recipient-title">Add Recipient</h3>
        
        <div className="form-group">
          <label htmlFor="name" className="form-label">Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={newRecipient.name}
            onChange={handleInputChange}
            placeholder="Recipient's name"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={newRecipient.email}
            onChange={handleInputChange}
            placeholder="Recipient's email address"
            className="form-control"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="message" className="form-label">Message (Optional)</label>
          <textarea
            id="message"
            name="message"
            value={newRecipient.message}
            onChange={handleInputChange}
            placeholder="Add a personal message"
            className="form-control"
            rows="3"
          />
        </div>
        
        <div className="add-recipient-actions">
          <Button 
            onClick={handleAddRecipient}
            variant="secondary"
          >
            Add Recipient
          </Button>
        </div>
      </Card>
      
      {recipients.length > 0 && (
        <div className="recipients-list-container">
          <h3 className="recipients-list-title">Recipients</h3>
          
          <div className="recipients-list">
            {recipients.filter(r => r.email && r.email.trim() !== '').map((recipient, index) => (
              <div key={index} className="recipient-item">
                <div className="recipient-info">
                  <div className="recipient-name">
                    {recipient.name}
                    {recipient.isSelf && " (You)"}
                  </div>
                  <div className="recipient-email">{recipient.email}</div>
                  {recipient.message && (
                    <div className="recipient-message">{recipient.message}</div>
                  )}
                </div>
                <Button 
                  onClick={() => handleRemoveRecipient(index)}
                  variant="tertiary"
                  size="small"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="recipient-manager-actions">
        <Button 
          onClick={handleContinue}
          disabled={recipients.filter(r => r.email && r.email.trim() !== '').length === 0 || loading}
          loading={loading}
        >
          Continue to Add Signature Fields
        </Button>
      </div>
    </div>
  );
};

export default RecipientManager;
